'use client';

import { useAuth } from '@/layers/presentation/context/AuthContext';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { ensureAmplifyConfigured } from '@/layers/presentation/utils/ensureAmplifyConfigured';
import { SelfServiceUserType } from '@/layers/support/authz/userType';
import logger from '@/layers/support/monitoring/logger';
import { UserType } from '@/types/modelEnums';
import { Button, Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / UserTypePrompt]';

/** Asks a signed-in user with no account type whether they are a Patient or a Provider. */
export function UserTypePrompt() {
  const { needsUserType, userTypeOptions, chooseUserType } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();
  const [saving, setSaving] = useState<SelfServiceUserType | null>(null);
  const [failed, setFailed] = useState(false);

  if (!needsUserType) return null;

  const labels: Record<SelfServiceUserType, string> = {
    [UserType.PATIENT]: t('users:userType.patient', 'Patient'),
    [UserType.PROVIDER]: t('users:userType.provider', 'Provider'),
  };

  const choose = async (type: SelfServiceUserType) => {
    setSaving(type);
    setFailed(false);
    try {
      await chooseUserType(type);
    } catch (error) {
      logger.error(`${LOG_MODULE} Failed to set the user type`, { error });
      setFailed(true);
    } finally {
      setSaving(null);
    }
  };

  const signOut = async () => {
    try {
      await ensureAmplifyConfigured();
      const { signOut: amplifySignOut } = await import('aws-amplify/auth');
      await amplifySignOut();
      router.push('/');
    } catch (error) {
      logger.error(`${LOG_MODULE} Failed to sign out`, { error });
    }
  };

  return (
    <Modal isOpen isDismissable={false} isKeyboardDismissDisabled hideCloseButton>
      <ModalContent>
        <ModalHeader>{t('users:userType.title', 'Finish setting up your account')}</ModalHeader>
        <ModalBody className="flex flex-col gap-4 px-8 pb-8">
          <p>{t('users:userType.subtitle', 'Are you signing up as a patient or a provider?')}</p>
          <div className="flex gap-4">
            {userTypeOptions.map(type => (
              <Button
                key={type}
                className="flex-1"
                color="primary"
                isDisabled={saving !== null}
                isLoading={saving === type}
                onPress={() => choose(type)}
              >
                {labels[type]}
              </Button>
            ))}
          </div>
          {!userTypeOptions.includes(UserType.PROVIDER) && (
            <p className="text-sm text-default-500">
              {t(
                'users:userType.providerNeedsInstitutionalEmail',
                'Provider accounts need an institutional email address.',
              )}
            </p>
          )}
          {failed && (
            <p role="alert" className="text-danger">
              {t('users:userType.error', 'We could not save that. Please try again.')}
            </p>
          )}
          <Button variant="light" isDisabled={saving !== null} onPress={signOut}>
            {t('users:userType.signOut', 'Sign out')}
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
